/**
 * THE TRADE TAXONOMY.
 * ============================================================================
 * Ported from contractor.wiki's service taxonomy so the two products describe
 * the same trades with the same words. One tree, and the only place trade
 * names, slugs and their sub-services are declared — the builder's first two
 * questions render straight from it.
 *
 * `icon` is a key into the glyph set in components/builder/TradeIcon.js rather
 * than an imported component, so this file stays plain data and can be sent to
 * a Worker or an API later without dragging React along with it.
 *
 * A trade with no `services` of its own is a supported state, not a gap: the
 * builder offers the trade's own name as the first service instead of padding
 * the list with invented ones.
 */

import { servicesFromCopy } from "./service-copy"

export const TRADES = [
  {
    slug: "roofing",
    name: "Roofing",
    icon: "house",
    description: "Repair, replacement & installation",
    services: [
      "Roof Repair",
      "Roof Replacement",
      "New Roof Installation",
      "Roof Inspection",
      "Storm Damage",
      "Gutter Installation",
      "Metal Roofing",
      "Flat Roofing",
      "Soffit & Fascia",
    ],
  },
  {
    slug: "plumbing",
    name: "Plumbing",
    icon: "wrench",
    description: "Repairs, installations & drainage",
    services: [
      "Leak Repair",
      "Drain Cleaning",
      "Water Heaters",
      "Fixture Installation",
      "Bathroom Plumbing",
      "Emergency Call-outs",
      "Repiping",
    ],
  },
  {
    slug: "hvac",
    name: "HVAC",
    icon: "snowflake",
    description: "Heating, cooling & ventilation",
    services: [
      "AC Repair",
      "Heating Repair",
      "System Replacement",
      "Annual Maintenance",
      "Duct Cleaning",
      "Heat Pumps",
      "Thermostat Installation",
    ],
  },
  {
    slug: "electrical",
    name: "Electrical",
    icon: "bolt",
    description: "Wiring, lighting & repairs",
    services: [
      "Panel Upgrades",
      "Rewiring",
      "Lighting",
      "EV Chargers",
      "Outlets & Switches",
      "Ceiling Fans",
      "Generator Installation",
    ],
  },
  {
    slug: "painting",
    name: "Painting",
    icon: "roller",
    description: "Interior & exterior painting",
    services: [
      "Interior Painting",
      "Exterior Painting",
      "Cabinet Refinishing",
      "Deck Staining",
      "Drywall Repair",
      "Pressure Washing",
    ],
  },
  {
    slug: "remodeling",
    name: "Remodeling",
    icon: "hammer",
    description: "Renovations, upgrades & repairs",
    services: [
      "Kitchen Remodeling",
      "Bathroom Remodeling",
      "Whole-home Renovation",
      "Windows",
      "Basement Finishing",
      "Flooring",
      "Decks & Patios",
    ],
  },
  {
    slug: "landscaping",
    name: "Landscaping",
    icon: "tree",
    description: "Lawns, gardens & outdoor spaces",
    services: [
      "Lawn Care",
      "Garden Design",
      "Tree Trimming",
      "Irrigation",
      "Hardscaping",
      "Seasonal Clean-up",
    ],
  },
  {
    slug: "fencing",
    name: "Fencing",
    icon: "fence",
    description: "Installation, repair & replacement",
    services: [
      "Fence Installation",
      "Fence Repair",
      "Gate Installation",
      "Privacy Fencing",
      "Chain Link",
      "Wood Fencing",
    ],
  },
  {
    slug: "exteriors",
    name: "Exteriors",
    icon: "house",
    description: "Siding, windows, roofing & gutters",
    services: [
      "Siding",
      "Roofing",
      "Windows",
      "Gutters",
      "Soffit & Fascia",
      "Doors",
      "Storm Damage",
    ],
  },
  {
    slug: "concrete",
    name: "Concrete",
    icon: "slab",
    description: "Driveways, slabs & foundations",
    services: [
      "Driveways",
      "Patios & Walkways",
      "Foundations",
      "Concrete Repair",
      "Stamped Concrete",
      "Retaining Walls",
    ],
  },
  {
    slug: "general",
    name: "General Contracting",
    icon: "building",
    description: "Whole projects, managed end to end",
    services: [
      "Home Additions",
      "Project Management",
      "Structural Repairs",
      "Permits & Inspections",
      "Custom Builds",
    ],
  },
  /* ── ADDED WITH THE SERVICE DROP — MTC-FCS-052 ────────────────────────────
     Thirteen trades that came with data/services-organized.json. They declare
     no `services` of their own on purpose: their list IS the drop, read
     through servicesForTrade() below, so there is one place those names live
     and no hand-copied second version to fall out of date.

     Telecommunications and Water & Wastewater arrived in the same drop with a
     single service each and were left out, as asked. */
  {
    slug: "flooring",
    name: "Flooring",
    icon: "planks",
    description: "Hardwood, vinyl, laminate & carpet",
    services: [],
  },
  {
    slug: "handyman",
    name: "Handyman",
    icon: "toolbox",
    description: "Repairs, odd jobs & maintenance",
    services: [],
  },
  {
    slug: "carpentry",
    name: "Carpentry",
    icon: "hammer",
    description: "Framing, trim & custom woodwork",
    services: [],
  },
  {
    slug: "cleaning",
    name: "Cleaning",
    icon: "broom",
    description: "Homes, offices & post-build cleans",
    services: [],
  },
  {
    slug: "drywall",
    name: "Drywall",
    icon: "panel",
    description: "Hanging, taping & texture",
    services: [],
  },
  {
    slug: "cabinetry",
    name: "Cabinetry",
    icon: "cabinet",
    description: "Kitchens, built-ins & refinishing",
    services: [],
  },
  {
    slug: "tile-stone",
    name: "Tile & Stone",
    icon: "tiles",
    description: "Tiling, showers & stonework",
    services: [],
  },
  {
    slug: "masonry",
    name: "Masonry",
    icon: "bricks",
    description: "Brick, block & stone",
    services: [],
  },
  {
    slug: "excavation",
    name: "Excavation",
    icon: "digger",
    description: "Digging, grading & site prep",
    services: [],
  },
  {
    slug: "demolition",
    name: "Demolition",
    icon: "hammer",
    description: "Strip-outs & tear-downs",
    services: [],
  },
  {
    slug: "welding",
    name: "Welding & Fabrication",
    icon: "spark",
    description: "Metalwork, repairs & fabrication",
    services: [],
  },
  {
    slug: "snow-removal",
    name: "Snow Removal",
    icon: "snowflake",
    description: "Ploughing, salting & clearing",
    services: [],
  },
  {
    slug: "property-management",
    name: "Property Management",
    icon: "keys",
    description: "Looking after buildings and tenants",
    services: [],
  },
]

export function tradeBySlug(slug) {
  return TRADES.find(t => t.slug === slug)
}

/**
 * Suggestions for one trade — MTC-FCS-052.
 *
 * The curated list first, then anything the service drop knows that is not
 * already on it. Order matters: the eleven original trades were written to
 * read the way a contractor would say them, and the drop is raw text taken
 * from how businesses describe themselves, so the hand-written names stay at
 * the top where the eye lands. Nothing is thrown away either way.
 *
 * A trade with neither still falls back to its own name — see the note at the
 * top of this file.
 */
export function servicesForTrade(slug) {
  const trade = tradeBySlug(slug)
  if (!trade) return []

  const seen = new Set(trade.services.map(name => name.toLowerCase()))
  const extra = servicesFromCopy(slug).filter(name => !seen.has(name.toLowerCase()))
  const all = [...trade.services, ...extra]

  return all.length > 0 ? all : [trade.name]
}
